/**
 * TypeScript mirror of src/iERP.QboGateway.Api/Domain/Enums.cs.
 *
 * The API uses System.Text.Json's default enum handling, so every enum crosses the wire as a
 * NUMBER. These are plain numeric TS enums with matching values, plus label maps for display.
 */

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export enum GatewayRole {
  Auditor = 0,
  Operator = 1,
  Approver = 2,
  Admin = 3,
}

export const GatewayRoleLabels: Record<GatewayRole, string> = {
  [GatewayRole.Auditor]: 'Auditor',
  [GatewayRole.Operator]: 'Operator',
  [GatewayRole.Approver]: 'Approver',
  [GatewayRole.Admin]: 'Administrator',
};

export const GatewayRoleDescriptions: Record<GatewayRole, string> = {
  [GatewayRole.Auditor]: 'Read-only access to every screen.',
  [GatewayRole.Operator]: 'Uploads, reviews, edits and posts documents.',
  [GatewayRole.Approver]: 'Operator rights plus approval decisions.',
  [GatewayRole.Admin]: 'Full control including connection, rules and users.',
};

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export enum DocumentStatus {
  Received = 0,
  Processing = 1,
  NeedsMorePages = 2,
  ReadyForReview = 3,
  PendingApproval = 4,
  Approved = 5,
  Posted = 6,
  Rejected = 7,
  Failed = 8,
  Cancelled = 9,
  /** A write to QuickBooks has been claimed and is in flight. Nothing may edit or post it. */
  Posting = 10,
  /**
   * The write was sent but no answer came back, so it is unknown whether QuickBooks created the
   * transaction. The idempotency reservation is kept and reconciliation resolves it; nothing may
   * post it again until then.
   */
  PostingOutcomeUnknown = 11,
}

export const DocumentStatusLabels: Record<DocumentStatus, string> = {
  [DocumentStatus.Received]: 'Received',
  [DocumentStatus.Processing]: 'Processing',
  [DocumentStatus.NeedsMorePages]: 'Needs more pages',
  [DocumentStatus.ReadyForReview]: 'Ready for review',
  [DocumentStatus.PendingApproval]: 'Pending approval',
  [DocumentStatus.Approved]: 'Approved',
  [DocumentStatus.Posted]: 'Posted',
  [DocumentStatus.Rejected]: 'Rejected',
  [DocumentStatus.Failed]: 'Failed',
  [DocumentStatus.Cancelled]: 'Cancelled',
  [DocumentStatus.Posting]: 'Posting to QuickBooks',
  [DocumentStatus.PostingOutcomeUnknown]: 'Confirming with QuickBooks',
};

/** Chip tone per status, consumed by the StatusChip component. */
export const DocumentStatusTones: Record<DocumentStatus, ChipTone> = {
  [DocumentStatus.Received]: 'neutral',
  [DocumentStatus.Processing]: 'info',
  [DocumentStatus.NeedsMorePages]: 'warn',
  [DocumentStatus.ReadyForReview]: 'info',
  [DocumentStatus.PendingApproval]: 'warn',
  [DocumentStatus.Approved]: 'good',
  [DocumentStatus.Posted]: 'good',
  [DocumentStatus.Rejected]: 'bad',
  [DocumentStatus.Failed]: 'bad',
  [DocumentStatus.Cancelled]: 'neutral',
  // Work in flight, not a failure: same tone as Processing.
  [DocumentStatus.Posting]: 'info',
  // Unresolved and awaiting reconciliation. Warn rather than fail: it may well have posted.
  [DocumentStatus.PostingOutcomeUnknown]: 'warn',
};

export enum GatewayScenario {
  NewVendor = 0,
  NewBill = 1,
  ExistingBill = 2,
  Expense = 3,
  PurchaseOrderMatch = 4,
}

export const GatewayScenarioLabels: Record<GatewayScenario, string> = {
  [GatewayScenario.NewVendor]: 'New vendor',
  [GatewayScenario.NewBill]: 'New bill',
  [GatewayScenario.ExistingBill]: 'Existing bill',
  [GatewayScenario.Expense]: 'Expense',
  // The gateway creates a bill and links it to the purchase order. It does not compare lines,
  // quantities, prices or items, so this is a link and not a match.
  [GatewayScenario.PurchaseOrderMatch]: 'Link to purchase order',
};

export const GatewayScenarioDescriptions: Record<GatewayScenario, string> = {
  [GatewayScenario.NewVendor]: 'Create or update a QuickBooks vendor from the document.',
  [GatewayScenario.NewBill]: 'Create a new QuickBooks bill from the invoice.',
  [GatewayScenario.ExistingBill]: 'Attach the extraction to a bill that already exists in QuickBooks.',
  [GatewayScenario.Expense]: 'Create a QuickBooks purchase (expense) transaction.',
  [GatewayScenario.PurchaseOrderMatch]:
    'Create the bill and link it to an open purchase order in QuickBooks. Lines are not compared to the purchase order.',
};

export enum QboEntityType {
  Vendor = 0,
  Bill = 1,
  PurchaseOrder = 2,
  Purchase = 3,
  BillPayment = 4,
  // PHASE 1LG additions — appended, never inserted, because the numeric values are persisted on
  // sync history and evidence rows (mirrors Domain/Enums.cs QboEntityType exactly).
  Account = 5,
  VendorCredit = 6,
  Attachable = 7,
}

export const QboEntityTypeLabels: Record<QboEntityType, string> = {
  [QboEntityType.Vendor]: 'Vendor',
  [QboEntityType.Bill]: 'Bill',
  [QboEntityType.PurchaseOrder]: 'Purchase order',
  [QboEntityType.Purchase]: 'Expense',
  [QboEntityType.BillPayment]: 'Bill payment',
  [QboEntityType.Account]: 'Account',
  [QboEntityType.VendorCredit]: 'Vendor credit',
  [QboEntityType.Attachable]: 'Attachment',
};

/** The five entity types the AI Learning historical evidence pipeline pulls, in the order the backend runs them. */
export const LEARNING_EVIDENCE_ENTITY_TYPES: QboEntityType[] = [
  QboEntityType.Bill,
  QboEntityType.Purchase,
  QboEntityType.VendorCredit,
  QboEntityType.BillPayment,
  QboEntityType.PurchaseOrder,
];

export enum IntakeChannel {
  Upload = 0,
  QboAttachment = 1,
  QboWebhook = 2,
  EmailIntake = 3,
  Api = 4,
}

export const IntakeChannelLabels: Record<IntakeChannel, string> = {
  [IntakeChannel.Upload]: 'Upload',
  [IntakeChannel.QboAttachment]: 'QBO attachment',
  [IntakeChannel.QboWebhook]: 'QBO webhook',
  [IntakeChannel.EmailIntake]: 'Email intake',
  [IntakeChannel.Api]: 'API',
};

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export enum SyncDirection {
  Pull = 0,
  Push = 1,
}

export const SyncDirectionLabels: Record<SyncDirection, string> = {
  [SyncDirection.Pull]: 'Pull',
  [SyncDirection.Push]: 'Push',
};

export enum SyncOutcome {
  Pending = 0,
  Succeeded = 1,
  Failed = 2,
  Skipped = 3,
}

export const SyncOutcomeLabels: Record<SyncOutcome, string> = {
  [SyncOutcome.Pending]: 'Pending',
  [SyncOutcome.Succeeded]: 'Succeeded',
  [SyncOutcome.Failed]: 'Failed',
  [SyncOutcome.Skipped]: 'Skipped',
};

export const SyncOutcomeTones: Record<SyncOutcome, ChipTone> = {
  [SyncOutcome.Pending]: 'info',
  [SyncOutcome.Succeeded]: 'good',
  [SyncOutcome.Failed]: 'bad',
  [SyncOutcome.Skipped]: 'neutral',
};

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

export enum ApprovalStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  InfoRequested = 3,
  Withdrawn = 4,
  /** The document changed after this approval was raised, so the approval no longer applies. */
  Superseded = 5,
}

export const ApprovalStatusLabels: Record<ApprovalStatus, string> = {
  [ApprovalStatus.Pending]: 'Pending',
  [ApprovalStatus.Approved]: 'Approved',
  [ApprovalStatus.Rejected]: 'Rejected',
  [ApprovalStatus.InfoRequested]: 'Info requested',
  [ApprovalStatus.Withdrawn]: 'Withdrawn',
  [ApprovalStatus.Superseded]: 'Superseded',
};

export const ApprovalStatusTones: Record<ApprovalStatus, ChipTone> = {
  [ApprovalStatus.Pending]: 'warn',
  [ApprovalStatus.Approved]: 'good',
  [ApprovalStatus.Rejected]: 'bad',
  [ApprovalStatus.InfoRequested]: 'info',
  [ApprovalStatus.Withdrawn]: 'neutral',
  // Spent rather than refused: same tone as Withdrawn, which also means "no longer applies".
  [ApprovalStatus.Superseded]: 'neutral',
};

export enum ApprovalReason {
  ManualReferral = 0,
  LowConfidence = 1,
  AmountThreshold = 2,
  NewVendor = 3,
  ValidationIssues = 4,
  RuleRequiresApproval = 5,
  SuspectedDuplicate = 6,
  /** Re-referred because the document changed after a previous approval was raised. */
  DocumentChanged = 7,
}

export const ApprovalReasonLabels: Record<ApprovalReason, string> = {
  [ApprovalReason.ManualReferral]: 'Manual referral',
  [ApprovalReason.LowConfidence]: 'Low confidence',
  [ApprovalReason.AmountThreshold]: 'Amount threshold',
  [ApprovalReason.NewVendor]: 'New vendor',
  [ApprovalReason.ValidationIssues]: 'Validation issues',
  [ApprovalReason.RuleRequiresApproval]: 'Rule requires approval',
  [ApprovalReason.SuspectedDuplicate]: 'Suspected duplicate',
  [ApprovalReason.DocumentChanged]: 'Document changed',
};

export const ApprovalReasonTones: Record<ApprovalReason, ChipTone> = {
  [ApprovalReason.ManualReferral]: 'neutral',
  [ApprovalReason.LowConfidence]: 'warn',
  [ApprovalReason.AmountThreshold]: 'info',
  [ApprovalReason.NewVendor]: 'info',
  [ApprovalReason.ValidationIssues]: 'warn',
  [ApprovalReason.RuleRequiresApproval]: 'info',
  [ApprovalReason.SuspectedDuplicate]: 'bad',
  [ApprovalReason.DocumentChanged]: 'warn',
};

/** Copy shown wherever an approval has been outrun by an edit to its document. */
export const STALE_APPROVAL_TITLE = 'This approval is no longer valid';

export const STALE_APPROVAL_DETAIL =
  'Document changed since this was sent for approval — it needs sending again.';

// ---------------------------------------------------------------------------
// QuickBooks connection
// ---------------------------------------------------------------------------

export enum QboConnectionStatus {
  NotConnected = 0,
  Connected = 1,
  ExpiringSoon = 2,
  Expired = 3,
  Revoked = 4,
  Error = 5,
}

export const QboConnectionStatusLabels: Record<QboConnectionStatus, string> = {
  [QboConnectionStatus.NotConnected]: 'Not connected',
  [QboConnectionStatus.Connected]: 'Connected',
  [QboConnectionStatus.ExpiringSoon]: 'Re-consent required soon',
  [QboConnectionStatus.Expired]: 'Expired',
  [QboConnectionStatus.Revoked]: 'Revoked',
  [QboConnectionStatus.Error]: 'Error',
};

export const QboConnectionStatusTones: Record<QboConnectionStatus, ChipTone> = {
  [QboConnectionStatus.NotConnected]: 'neutral',
  [QboConnectionStatus.Connected]: 'good',
  [QboConnectionStatus.ExpiringSoon]: 'warn',
  [QboConnectionStatus.Expired]: 'bad',
  [QboConnectionStatus.Revoked]: 'bad',
  [QboConnectionStatus.Error]: 'bad',
};

// ---------------------------------------------------------------------------
// Tenant provisioning (mirrors Domain/IdentityEntities.cs)
// ---------------------------------------------------------------------------

export enum TenantProvisioningStatus {
  /** Created by signup, nothing sent to the bot yet. */
  Pending = 0,
  /** Registration staged with the bot; waiting on a processing credential. */
  Registering = 1,
  /** The bot is holding the registration until its own verification email is clicked. */
  AwaitingLicenceVerification = 2,
  /** Credential issued. The tenant can submit documents. */
  Ready = 3,
  /** Registration failed. Carries a reason and is retried by the worker. */
  Failed = 4,
  /** Suspended by iERP. Blocks submission regardless of licence. */
  Suspended = 5,
}

export const TenantProvisioningStatusLabels: Record<TenantProvisioningStatus, string> = {
  [TenantProvisioningStatus.Pending]: 'Pending',
  [TenantProvisioningStatus.Registering]: 'Registering',
  [TenantProvisioningStatus.AwaitingLicenceVerification]: 'Awaiting licence verification',
  [TenantProvisioningStatus.Ready]: 'Ready',
  [TenantProvisioningStatus.Failed]: 'Failed',
  [TenantProvisioningStatus.Suspended]: 'Suspended',
};

export const TenantProvisioningStatusTones: Record<TenantProvisioningStatus, ChipTone> = {
  [TenantProvisioningStatus.Pending]: 'info',
  [TenantProvisioningStatus.Registering]: 'info',
  [TenantProvisioningStatus.AwaitingLicenceVerification]: 'warn',
  [TenantProvisioningStatus.Ready]: 'good',
  [TenantProvisioningStatus.Failed]: 'bad',
  [TenantProvisioningStatus.Suspended]: 'bad',
};

/**
 * Whether the tenant may use the product's features right now, and if not, why. Computed by the
 * server (`TenantVerificationFilter.Evaluate`) and served on the onboarding DTO — the SPA displays
 * this value and never re-implements the licensing rule.
 */
export enum TenantFeatureAccess {
  /** Ready, active, and the licence is either active or not yet reported. */
  Enabled = 0,
  /** Provisioning has not reached Ready: the verification email is the way forward. */
  Unverified = 1,
  /** Ready, but the refreshed licence is reported inactive: contact iERP. */
  LicenceInactive = 2,
  /** The tenant is suspended or deactivated: contact iERP. */
  Suspended = 3,
}

// ---------------------------------------------------------------------------
// Sync rules
// ---------------------------------------------------------------------------

export enum RuleField {
  InvoiceTotal = 0,
  ExtractionConfidence = 1,
  VendorName = 2,
  VendorExistsInQbo = 3,
  DocumentScenario = 4,
  CurrencyCode = 5,
  HasValidationIssues = 6,
  LineCount = 7,
  SourceEmailDomain = 8,
  HasLowConfidenceLines = 9,
  DoesNotReconcile = 10,
}

export const RuleFieldLabels: Record<RuleField, string> = {
  [RuleField.InvoiceTotal]: 'Invoice total',
  [RuleField.ExtractionConfidence]: 'Extraction confidence',
  [RuleField.VendorName]: 'Vendor name',
  [RuleField.VendorExistsInQbo]: 'Vendor exists in QBO',
  [RuleField.DocumentScenario]: 'Document scenario',
  [RuleField.CurrencyCode]: 'Currency code',
  [RuleField.HasValidationIssues]: 'Has validation issues',
  [RuleField.LineCount]: 'Line count',
  [RuleField.SourceEmailDomain]: 'Source email domain',
  [RuleField.HasLowConfidenceLines]: 'Has low-confidence lines',
  [RuleField.DoesNotReconcile]: 'Lines do not reconcile to total',
};

export enum RuleOperator {
  Equals = 0,
  NotEquals = 1,
  GreaterThan = 2,
  GreaterThanOrEqual = 3,
  LessThan = 4,
  LessThanOrEqual = 5,
  Contains = 6,
  StartsWith = 7,
  IsTrue = 8,
  IsFalse = 9,
}

export const RuleOperatorLabels: Record<RuleOperator, string> = {
  [RuleOperator.Equals]: 'equals',
  [RuleOperator.NotEquals]: 'does not equal',
  [RuleOperator.GreaterThan]: 'is greater than',
  [RuleOperator.GreaterThanOrEqual]: 'is at least',
  [RuleOperator.LessThan]: 'is less than',
  [RuleOperator.LessThanOrEqual]: 'is at most',
  [RuleOperator.Contains]: 'contains',
  [RuleOperator.StartsWith]: 'starts with',
  [RuleOperator.IsTrue]: 'is true',
  [RuleOperator.IsFalse]: 'is false',
};

/** Value kind each rule field carries, used to drive the right editor and operator list. */
export type RuleFieldKind = 'number' | 'text' | 'boolean' | 'scenario';

export const RuleFieldKinds: Record<RuleField, RuleFieldKind> = {
  [RuleField.InvoiceTotal]: 'number',
  [RuleField.ExtractionConfidence]: 'number',
  [RuleField.VendorName]: 'text',
  [RuleField.VendorExistsInQbo]: 'boolean',
  [RuleField.DocumentScenario]: 'scenario',
  [RuleField.CurrencyCode]: 'text',
  [RuleField.HasValidationIssues]: 'boolean',
  [RuleField.LineCount]: 'number',
  [RuleField.SourceEmailDomain]: 'text',
  [RuleField.HasLowConfidenceLines]: 'boolean',
  [RuleField.DoesNotReconcile]: 'boolean',
};

const NUMBER_OPERATORS: RuleOperator[] = [
  RuleOperator.Equals,
  RuleOperator.NotEquals,
  RuleOperator.GreaterThan,
  RuleOperator.GreaterThanOrEqual,
  RuleOperator.LessThan,
  RuleOperator.LessThanOrEqual,
];

const TEXT_OPERATORS: RuleOperator[] = [
  RuleOperator.Equals,
  RuleOperator.NotEquals,
  RuleOperator.Contains,
  RuleOperator.StartsWith,
];

const BOOLEAN_OPERATORS: RuleOperator[] = [RuleOperator.IsTrue, RuleOperator.IsFalse];

const SCENARIO_OPERATORS: RuleOperator[] = [RuleOperator.Equals, RuleOperator.NotEquals];

/** Operators that make sense for a given rule field. */
export function operatorsForField(field: RuleField): RuleOperator[] {
  switch (RuleFieldKinds[field]) {
    case 'number':
      return NUMBER_OPERATORS;
    case 'boolean':
      return BOOLEAN_OPERATORS;
    case 'scenario':
      return SCENARIO_OPERATORS;
    case 'text':
    default:
      return TEXT_OPERATORS;
  }
}

/** True when the operator is self-contained and needs no comparison value. */
export function operatorNeedsValue(operator: RuleOperator): boolean {
  return operator !== RuleOperator.IsTrue && operator !== RuleOperator.IsFalse;
}

export enum RuleAction {
  AutoPost = 0,
  RequireApproval = 1,
  HoldForReview = 2,
  Skip = 3,
}

export const RuleActionLabels: Record<RuleAction, string> = {
  [RuleAction.AutoPost]: 'Auto-post to QuickBooks',
  [RuleAction.RequireApproval]: 'Require approval',
  [RuleAction.HoldForReview]: 'Hold for review',
  [RuleAction.Skip]: 'Skip document',
};

export const RuleActionTones: Record<RuleAction, ChipTone> = {
  [RuleAction.AutoPost]: 'good',
  [RuleAction.RequireApproval]: 'warn',
  [RuleAction.HoldForReview]: 'info',
  [RuleAction.Skip]: 'neutral',
};

// ---------------------------------------------------------------------------
// Shared display helpers
// ---------------------------------------------------------------------------

export type ChipTone = 'neutral' | 'info' | 'good' | 'warn' | 'bad';

/** Numeric members of a numeric TS enum, in declaration order. Handy for building <select> lists. */
export function enumValues<T extends Record<string, string | number>>(e: T): number[] {
  return Object.values(e).filter((v): v is number => typeof v === 'number');
}


// ---------------------------------------------------------------------------
// Vendor invoice intake
// ---------------------------------------------------------------------------

/**
 * The one word a person reads about an invoice. DERIVED by the API from the document status, the
 * hand-off state, the pending approval and the review state - never stored, so it cannot drift out
 * of step with the document it describes.
 */
export enum VendorInvoiceIntakeStage {
  Received = 0,
  Processing = 1,
  NeedsReview = 2,
  VendorReview = 3,
  ReadyToPost = 4,
  PendingApproval = 5,
  Posted = 6,
  Held = 7,
  Failed = 8,
  Rejected = 9,
  OutcomeUnknown = 10,
}

export const VendorInvoiceIntakeStageLabels: Record<VendorInvoiceIntakeStage, string> = {
  [VendorInvoiceIntakeStage.Received]: 'Received',
  [VendorInvoiceIntakeStage.Processing]: 'Processing',
  [VendorInvoiceIntakeStage.NeedsReview]: 'Needs review',
  [VendorInvoiceIntakeStage.VendorReview]: 'Vendor review',
  [VendorInvoiceIntakeStage.ReadyToPost]: 'Ready to post',
  [VendorInvoiceIntakeStage.PendingApproval]: 'Pending approval',
  [VendorInvoiceIntakeStage.Posted]: 'Posted',
  [VendorInvoiceIntakeStage.Held]: 'Held',
  [VendorInvoiceIntakeStage.Failed]: 'Failed',
  [VendorInvoiceIntakeStage.Rejected]: 'Rejected',
  [VendorInvoiceIntakeStage.OutcomeUnknown]: 'Outcome unknown',
};

export const VendorInvoiceIntakeStageTones: Record<VendorInvoiceIntakeStage, ChipTone> = {
  [VendorInvoiceIntakeStage.Received]: 'neutral',
  [VendorInvoiceIntakeStage.Processing]: 'info',
  [VendorInvoiceIntakeStage.NeedsReview]: 'warn',
  [VendorInvoiceIntakeStage.VendorReview]: 'warn',
  [VendorInvoiceIntakeStage.ReadyToPost]: 'good',
  [VendorInvoiceIntakeStage.PendingApproval]: 'warn',
  [VendorInvoiceIntakeStage.Posted]: 'good',
  [VendorInvoiceIntakeStage.Held]: 'warn',
  [VendorInvoiceIntakeStage.Failed]: 'bad',
  [VendorInvoiceIntakeStage.Rejected]: 'bad',
  [VendorInvoiceIntakeStage.OutcomeUnknown]: 'bad',
};

/** The stages worth offering as a filter, in the order the work flows. */
export const VendorInvoiceIntakeStageFilters: VendorInvoiceIntakeStage[] = [
  VendorInvoiceIntakeStage.NeedsReview,
  VendorInvoiceIntakeStage.VendorReview,
  VendorInvoiceIntakeStage.ReadyToPost,
  VendorInvoiceIntakeStage.PendingApproval,
  VendorInvoiceIntakeStage.Held,
  VendorInvoiceIntakeStage.Received,
  VendorInvoiceIntakeStage.Processing,
  VendorInvoiceIntakeStage.Failed,
  VendorInvoiceIntakeStage.OutcomeUnknown,
  VendorInvoiceIntakeStage.Posted,
  VendorInvoiceIntakeStage.Rejected,
];

export enum IntakeLineReviewState {
  NotReviewed = 0,
  Confirmed = 1,
  Corrected = 2,
  Excluded = 3,
}

export const IntakeLineReviewStateLabels: Record<IntakeLineReviewState, string> = {
  [IntakeLineReviewState.NotReviewed]: 'Not reviewed',
  [IntakeLineReviewState.Confirmed]: 'Confirmed',
  [IntakeLineReviewState.Corrected]: 'Corrected',
  [IntakeLineReviewState.Excluded]: 'Excluded',
};

export const IntakeLineReviewStateTones: Record<IntakeLineReviewState, ChipTone> = {
  [IntakeLineReviewState.NotReviewed]: 'warn',
  [IntakeLineReviewState.Confirmed]: 'good',
  [IntakeLineReviewState.Corrected]: 'good',
  [IntakeLineReviewState.Excluded]: 'neutral',
};

export enum IntakeLineType {
  Account = 0,
  Item = 1,
}

export const IntakeLineTypeLabels: Record<IntakeLineType, string> = {
  [IntakeLineType.Account]: 'Account',
  [IntakeLineType.Item]: 'Product / service',
};

export enum LineRecommendationStatus {
  None = 0,
  Suggested = 1,
  Accepted = 2,
  Overridden = 3,
  Unusable = 4,
}

export const LineRecommendationStatusLabels: Record<LineRecommendationStatus, string> = {
  [LineRecommendationStatus.None]: 'No suggestion',
  [LineRecommendationStatus.Suggested]: 'Suggested',
  [LineRecommendationStatus.Accepted]: 'Suggestion kept',
  [LineRecommendationStatus.Overridden]: 'Coded elsewhere',
  [LineRecommendationStatus.Unusable]: 'Suggestion unusable',
};

/**
 * Where the tax on an invoice stands. Tax is REVIEWED rather than trusted: the figure a supplier
 * printed and the figure this company's own tax codes give are two different numbers, and a
 * difference is an accounting question rather than a rounding nuisance.
 */
export enum TaxReviewStatus {
  NotCalculated = 0,
  Matches = 1,
  Differs = 2,
  Accepted = 3,
  Invalidated = 4,
  UnknownTaxCode = 5,
  /**
   * A coded line carries a tax code this company DOES have, but whose purchase tax the gateway
   * cannot state - a compound treatment, or a code QuickBooks reports no purchase rate for. No
   * figure was produced, so nothing has been verified.
   */
  UncomputableTax = 6,
}

export const TaxReviewStatusLabels: Record<TaxReviewStatus, string> = {
  [TaxReviewStatus.NotCalculated]: 'Not checked',
  [TaxReviewStatus.Matches]: 'Agrees',
  [TaxReviewStatus.Differs]: 'Differs',
  [TaxReviewStatus.Accepted]: 'Difference accepted',
  [TaxReviewStatus.Invalidated]: 'Needs rechecking',
  [TaxReviewStatus.UnknownTaxCode]: 'Unknown tax code',
  [TaxReviewStatus.UncomputableTax]: 'Cannot be computed',
};

export const TaxReviewStatusTones: Record<TaxReviewStatus, ChipTone> = {
  [TaxReviewStatus.NotCalculated]: 'neutral',
  [TaxReviewStatus.Matches]: 'good',
  [TaxReviewStatus.Differs]: 'warn',
  [TaxReviewStatus.Accepted]: 'info',
  [TaxReviewStatus.Invalidated]: 'warn',
  [TaxReviewStatus.UnknownTaxCode]: 'bad',
  [TaxReviewStatus.UncomputableTax]: 'warn',
};

/**
 * What happened when a line's tax code was last resolved. Distinguishes "not in this company" from
 * "in this company, but its treatment cannot be computed" - which need opposite actions from the
 * reviewer, so telling them the wrong one sends them to correct something already right.
 */
export enum LineTaxResolution {
  NotCoded = 0,
  Resolved = 1,
  CodeNotInCompany = 2,
  RateNotComputable = 3,
}

export const LineTaxResolutionLabels: Record<LineTaxResolution, string> = {
  [LineTaxResolution.NotCoded]: 'No tax code',
  [LineTaxResolution.Resolved]: 'Rate resolved',
  [LineTaxResolution.CodeNotInCompany]: 'Not a code in this company',
  [LineTaxResolution.RateNotComputable]: 'Rate not computable here',
};

export const LineTaxResolutionTones: Record<LineTaxResolution, ChipTone> = {
  [LineTaxResolution.NotCoded]: 'neutral',
  [LineTaxResolution.Resolved]: 'good',
  [LineTaxResolution.CodeNotInCompany]: 'bad',
  [LineTaxResolution.RateNotComputable]: 'warn',
};

// ---------------------------------------------------------------------------
// Payables Agent
// ---------------------------------------------------------------------------

/**
 * How far ONE VENDOR has been trusted to move, on a ladder a human climbs one rung at a time.
 * There is no tenant-wide execution mode — every vendor starts at PayablesAgentSetupDto's
 * defaultNewVendorStage and a person explicitly advances that vendor, and only that vendor, after
 * watching how the stage below behaved. Nothing auto-advances.
 */
export enum VendorAgentStage {
  /** Learn from this vendor's payment history. Nothing is scheduled or paid. */
  Observing = 0,
  /** Suggest a date on new bills; a person confirms, reschedules or holds each one. Still nothing
   * is ever paid. */
  Scheduling = 1,
  /** Due items sit Ready for Payment; a person pays with an explicit Pay Now click. */
  ManualPay = 2,
  /** Due items are sent to the approval gate; a different person approves before a payment is
   * created. */
  ApprovalGated = 3,
  /** Due items at or below the tenant's auto-post cap pay themselves, unattended, on schedule. */
  AutoPost = 4,
}

export const VendorAgentStageLabels: Record<VendorAgentStage, string> = {
  [VendorAgentStage.Observing]: 'Observing',
  [VendorAgentStage.Scheduling]: 'Scheduling',
  [VendorAgentStage.ManualPay]: 'Manual pay',
  [VendorAgentStage.ApprovalGated]: 'Approval-gated',
  [VendorAgentStage.AutoPost]: 'Auto-post',
};

export const VendorAgentStageDescriptions: Record<VendorAgentStage, string> = {
  [VendorAgentStage.Observing]: 'Learning from history only. Nothing is scheduled or paid.',
  [VendorAgentStage.Scheduling]: 'Suggests a date on new bills for a person to confirm. Nothing is ever paid.',
  [VendorAgentStage.ManualPay]: 'Due bills sit Ready for Payment; a person pays with an explicit Pay Now click.',
  [VendorAgentStage.ApprovalGated]: 'Due bills are sent for approval; a different person approves before it pays.',
  [VendorAgentStage.AutoPost]: 'Due bills under the auto-post cap pay themselves on schedule, unattended.',
};

export const VendorAgentStageTones: Record<VendorAgentStage, ChipTone> = {
  [VendorAgentStage.Observing]: 'neutral',
  [VendorAgentStage.Scheduling]: 'info',
  [VendorAgentStage.ManualPay]: 'info',
  [VendorAgentStage.ApprovalGated]: 'warn',
  [VendorAgentStage.AutoPost]: 'good',
};

export enum ScheduledPaymentStatus {
  Proposed = 0,
  Scheduled = 1,
  OnHold = 2,
  ReadyForPayment = 3,
  PendingApproval = 4,
  Approved = 5,
  Posted = 6,
  Paid = 7,
  Cancelled = 8,
  PaidExternally = 9,
  /** Claimed for payment; exactly one caller may call QuickBooks for this bill. */
  Paying = 10,
  /** The QuickBooks call was not answered. A payment may exist; nothing retries automatically. */
  PaymentOutcomeUnknown = 11,
}

export const ScheduledPaymentStatusLabels: Record<ScheduledPaymentStatus, string> = {
  [ScheduledPaymentStatus.Proposed]: 'Proposed',
  [ScheduledPaymentStatus.Scheduled]: 'Scheduled',
  [ScheduledPaymentStatus.OnHold]: 'On hold',
  [ScheduledPaymentStatus.ReadyForPayment]: 'Ready for payment',
  [ScheduledPaymentStatus.PendingApproval]: 'Pending approval',
  [ScheduledPaymentStatus.Approved]: 'Approved',
  [ScheduledPaymentStatus.Posted]: 'Posted',
  [ScheduledPaymentStatus.Paid]: 'Paid',
  [ScheduledPaymentStatus.Cancelled]: 'Cancelled',
  [ScheduledPaymentStatus.PaidExternally]: 'Paid externally',
  [ScheduledPaymentStatus.Paying]: 'Paying',
  [ScheduledPaymentStatus.PaymentOutcomeUnknown]: 'Needs checking in QuickBooks',
};

export const ScheduledPaymentStatusTones: Record<ScheduledPaymentStatus, ChipTone> = {
  [ScheduledPaymentStatus.Proposed]: 'neutral',
  [ScheduledPaymentStatus.Scheduled]: 'info',
  [ScheduledPaymentStatus.OnHold]: 'warn',
  [ScheduledPaymentStatus.ReadyForPayment]: 'info',
  [ScheduledPaymentStatus.PendingApproval]: 'warn',
  [ScheduledPaymentStatus.Approved]: 'info',
  [ScheduledPaymentStatus.Posted]: 'good',
  [ScheduledPaymentStatus.Paid]: 'good',
  [ScheduledPaymentStatus.Cancelled]: 'neutral',
  [ScheduledPaymentStatus.PaidExternally]: 'good',
  [ScheduledPaymentStatus.Paying]: 'info',
  // Deliberately the loudest tone on the screen: money may already have moved for this bill.
  [ScheduledPaymentStatus.PaymentOutcomeUnknown]: 'bad',
};

export enum PaymentScheduleSource {
  History = 0,
  PaymentTerms = 1,
  User = 2,
}

export const PaymentScheduleSourceLabels: Record<PaymentScheduleSource, string> = {
  [PaymentScheduleSource.History]: 'Payment history',
  [PaymentScheduleSource.PaymentTerms]: 'Payment terms',
  [PaymentScheduleSource.User]: 'Set by a user',
};

export enum PayProfileConfidence {
  Low = 0,
  Medium = 1,
  High = 2,
}

export const PayProfileConfidenceLabels: Record<PayProfileConfidence, string> = {
  [PayProfileConfidence.Low]: 'Low',
  [PayProfileConfidence.Medium]: 'Medium',
  [PayProfileConfidence.High]: 'High',
};

export const PayProfileConfidenceTones: Record<PayProfileConfidence, ChipTone> = {
  [PayProfileConfidence.Low]: 'neutral',
  [PayProfileConfidence.Medium]: 'info',
  [PayProfileConfidence.High]: 'good',
};
